import { useState, useEffect, useRef } from 'react'; // search implementation
import { handleAppError } from '@/lib/errorUtils';
import { useAuth } from '@/contexts/AuthContext';
import { getStorageData, setStorageData, removeStorageData } from '@/lib/storage';

export interface SearchFormData {
  jobTitle: string;
  locationType: string;
  region: string;
  seniority: string;
  companySize: string;
  minSalary: string;
  visaSponsorship: string;
  skills: string;
  additionalInfo: string;
}

const DEFAULT_FORM_DATA = {
  jobTitle: '',
  locationType: 'Remote - In Country',
  region: '',
  seniority: 'Any',
  companySize: 'Any',
  minSalary: '',
  visaSponsorship: 'Flexible',
  skills: '',
  additionalInfo: ''
};

export function useSearch(workspaceId: string, onClose?: () => void, onSelectJobProps?: (link: string, title?: string, company?: string, description?: string) => Promise<void>) {
  const { user } = useAuth();
  const userId = user?.uid;

  const [formData, setFormData] = useState<SearchFormData>(DEFAULT_FORM_DATA);
  const [showLogin, setShowLogin] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchingMore, setIsSearchingMore] = useState(false);
  const [searchStatus, setSearchStatus] = useState("Scouring the web for active jobs...");
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastSearchTime, setLastSearchTime] = useState<Date | null>(null);
  const [isSelecting, setIsSelecting] = useState<string | null>(null);
  const [loadedScope, setLoadedScope] = useState<{ workspaceId: string, userId?: string } | null>(null);
  
  const abortControllerRef = useRef<AbortController | null>(null);

  // Load from localforage when workspaceId/user changes
  useEffect(() => {
    setLoadedScope(null);
    let isMounted = true;
    const loadCache = async () => {
      try {
        const cachedForm = await getStorageData<SearchFormData>(`job_search_form_${workspaceId}`, userId);
        const cachedResult = await getStorageData<any>(`job_search_result_${workspaceId}`, userId);
        const cachedTimeStr = await getStorageData<string>(`job_search_time_${workspaceId}`, userId);

        if (!isMounted) return;

        if (cachedForm) {
          setFormData(cachedForm);
        } else {
          setFormData(DEFAULT_FORM_DATA);
        }

        if (cachedResult) {
          setResult(cachedResult);
        } else {
          setResult(null);
        }

        if (cachedTimeStr) {
          setLastSearchTime(new Date(cachedTimeStr));
        } else {
          setLastSearchTime(null);
        }
      } catch (e) {
      } finally {
        if (isMounted) {
          setLoadedScope({ workspaceId, userId });
        }
      }
    };
    loadCache();
    return () => { isMounted = false; };
  }, [workspaceId, userId]);

  // Persist formData
  useEffect(() => {
    if (loadedScope?.workspaceId !== workspaceId || loadedScope?.userId !== userId) return;
    if (formData && formData !== DEFAULT_FORM_DATA) {
      setStorageData(`job_search_form_${workspaceId}`, formData, userId).catch(() => {});
    }
  }, [formData, workspaceId, userId, loadedScope]);

  // Persist result
  useEffect(() => {
    if (loadedScope?.workspaceId !== workspaceId || loadedScope?.userId !== userId) return;
    if (result) {
      setStorageData(`job_search_result_${workspaceId}`, result, userId).catch(() => {});
    } else {
      removeStorageData(`job_search_result_${workspaceId}`, userId).catch(() => {});
    }
  }, [result, workspaceId, userId, loadedScope]);

  // Persist time
  useEffect(() => {
    if (loadedScope?.workspaceId !== workspaceId || loadedScope?.userId !== userId) return;
    if (lastSearchTime) {
      setStorageData(`job_search_time_${workspaceId}`, lastSearchTime.toISOString(), userId).catch(() => {});
    } else {
      removeStorageData(`job_search_time_${workspaceId}`, userId).catch(() => {});
    }
  }, [lastSearchTime, workspaceId, userId, loadedScope]);

  useEffect(() => {
    if (!isSearching && !isSearchingMore) return;
    const statuses = [
      "Scouring the web for active jobs...",
      "Executing deep Google searches...",
      "Analyzing ATS platforms and job boards...",
      "Filtering out closed and expired listings...",
      "Verifying URL integrity and 404s...",
      "Extracting salaries and remote policies...",
      "Ranking matches against your criteria...",
      "Finalizing list..."
    ];
    let idx = 0;
    setSearchStatus(statuses[0]);
    const interval = setInterval(() => {
      idx = (idx + 1) % statuses.length;
      setSearchStatus(statuses[idx]);
    }, 3500);
    return () => clearInterval(interval);
  }, [isSearching, isSearchingMore]);

  const handleClear = () => {
    setFormData(DEFAULT_FORM_DATA);
    setResult(null);
    setError(null);
    setLastSearchTime(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancelSearch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const handleSearch = async () => {
    if (!formData.jobTitle.trim()) return;
    
    // Abort existing search if any
    handleCancelSearch();

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsSearching(true);
    setResult(null);
    setError(null);
    
    let effectiveRegion = formData.region;
    if (formData.locationType === 'Remote - In Country' && !effectiveRegion) {
      effectiveRegion = 'Unknown (Please assume US/UK/Canada or globally remote as fallback)';
    }

    const requirements = `
Job Title: ${formData.jobTitle}
Location Type: ${formData.locationType}
Target Region/Country: ${effectiveRegion || 'Any'}
Seniority: ${formData.seniority}
Company Size: ${formData.companySize}
Minimum Salary: ${formData.minSalary || 'Not specified'}
Visa Sponsorship needed: ${formData.visaSponsorship}
Key Skills/Tech Stack: ${formData.skills || 'Not specified'}
Additional Notes: ${formData.additionalInfo || 'None'}
    `.trim();

    try {
      const { searchForJobs } = await import('@/services/geminiService');
      const response = await searchForJobs(requirements, abortController.signal);
      setResult(response.data);
      setLastSearchTime(new Date());
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message?.includes('aborted')) {
        console.log('Search was cancelled');
        return; // Don't set error on cancel
      }
      const msg = handleAppError(err, 'Failed to search for jobs');
      setError(msg);
    } finally {
      if (abortControllerRef.current === abortController) {
        setIsSearching(false);
        abortControllerRef.current = null;
      }
    }
  };

  const handleLoadMore = async () => {
    if (!formData.jobTitle.trim() || !result) return;
    
    // Abort existing search if any
    handleCancelSearch();

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsSearchingMore(true);
    setError(null);
    
    let effectiveRegion = formData.region;
    if (formData.locationType === 'Remote - In Country' && !effectiveRegion) {
      effectiveRegion = 'Unknown (Please assume US/UK/Canada or globally remote as fallback)';
    }

    const existingCompanies = result.jobs ? result.jobs.map((j: any) => j.company).join(', ') : 'None';

    const requirements = `
Job Title: ${formData.jobTitle}
Location Type: ${formData.locationType}
Target Region/Country: ${effectiveRegion || 'Any'}
Seniority: ${formData.seniority}
Company Size: ${formData.companySize}
Minimum Salary: ${formData.minSalary || 'Not specified'}
Visa Sponsorship needed: ${formData.visaSponsorship}
Key Skills/Tech Stack: ${formData.skills || 'Not specified'}
Additional Notes: ${formData.additionalInfo || 'None'}
EXCLUDE THESE COMPANIES (Already found): ${existingCompanies}
    `.trim();

    try {
      const { searchForJobs } = await import('@/services/geminiService');
      const response = await searchForJobs(requirements, abortController.signal);
      
      setResult((prev: any) => {
         if (!prev) return response.data;
         
         const newJobs = response.data.jobs || [];
         const uniqueNewJobs = newJobs.filter((nj: any) => !prev.jobs.some((pj: any) => pj.sourceLink === nj.sourceLink || pj.company === nj.company));
         
         const combinedJobs = [...prev.jobs, ...uniqueNewJobs].slice(0, 100);
         return {
            ...prev,
            summary: {
               ...prev.summary,
               totalRecommended: combinedJobs.length,
               highPriority: combinedJobs.filter((j: any) => j.priority === 'High').length,
               mediumPriority: combinedJobs.filter((j: any) => j.priority === 'Medium').length,
               lowPriority: combinedJobs.filter((j: any) => j.priority === 'Low').length,
               manualVerification: combinedJobs.filter((j: any) => j.hardConstraintResult === 'Unclear').length
            },
            jobs: combinedJobs
         };
      });
      setLastSearchTime(new Date());
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message?.includes('aborted')) {
        console.log('Load more was cancelled');
        return; // Don't set error on cancel
      }
      const msg = handleAppError(err, 'Failed to load more jobs');
      setError(msg);
    } finally {
      if (abortControllerRef.current === abortController) {
        setIsSearchingMore(false);
        abortControllerRef.current = null;
      }
    }
  };

  const handleSelectJob = async (link: string, title?: string, company?: string, description?: string) => {
    if (!onSelectJobProps) return;
    
    try {
      setIsSelecting(link);
      await onSelectJobProps(link, title, company, description);
    } catch(err: any) {
      handleAppError(err, 'Could not fetch the job link automatically.');
    } finally {
      setIsSelecting(null);
    }
  };

  return {
    formData,
    setFormData,
    showLogin,
    setShowLogin,
    isSearching,
    isSearchingMore,
    searchStatus,
    result,
    error,
    lastSearchTime,
    isSelecting,
    handleClear,
    handleInputChange,
    handleSearch,
    handleCancelSearch,
    handleLoadMore,
    handleSelectJob
  };
}
